import { useDateFormat } from '@ja/core-ui/lib/date'

import type { CommunicationPriority } from '../types/communication'

/**
 * Shared formatting utilities for communication display components.
 * Thin wrapper over `useDateFormat()` plus communication-specific priority
 * styling.
 */
export function useCommunicationFormatters() {
  const df = useDateFormat()

  function formatRelativeDate(dateString: string | null): string {
    return df.formatRelative(dateString)
  }

  function formatFullDate(dateString: string | null): string {
    return df.formatLongDateTime(dateString)
  }

  function formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const priorityConfig: Record<CommunicationPriority, { classes: string; borderClass: string }> = {
    low: {
      classes: 'bg-muted text-muted-foreground',
      borderClass: 'border-l-transparent',
    },
    normal: {
      classes: 'bg-secondary text-secondary-foreground',
      borderClass: 'border-l-primary/30',
    },
    high: {
      classes: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-100',
      borderClass: 'border-l-amber-500',
    },
    urgent: {
      classes: 'bg-destructive text-destructive-foreground',
      borderClass: 'border-l-destructive',
    },
  }

  function getPriorityClasses(priority: CommunicationPriority): string {
    return priorityConfig[priority]?.classes ?? priorityConfig.normal.classes
  }

  function getPriorityBorderClass(priority: CommunicationPriority): string {
    return priorityConfig[priority]?.borderClass ?? priorityConfig.normal.borderClass
  }

  return {
    formatRelativeDate,
    formatFullDate,
    formatFileSize,
    getPriorityClasses,
    getPriorityBorderClass,
  }
}
